import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ShieldCheck, ArrowLeft, CheckCircle2, CreditCard } from "lucide-react";
import api from "@/api/client";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

export default function Checkout() {
  const { cart, refresh } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [address, setAddress] = useState(user?.address || "");
  const [city, setCity] = useState("Mumbai");
  const [step, setStep] = useState("address"); // address | payment | success
  const [order, setOrder] = useState(null);
  const [processing, setProcessing] = useState(false);

  if (cart.items.length === 0 && step === "address") {
    return (
      <div className="min-h-screen bg-zinc-100 p-10">
        <div className="max-w-lg bg-white border-2 border-zinc-900 p-8">
          <div className="tag">CHECKOUT</div>
          <h1 className="font-display text-3xl font-black tracking-tight mt-1">
            Nothing to checkout
          </h1>
          <p className="text-zinc-500 mt-2">Add items to cart to continue.</p>
          <button
            onClick={() => nav("/catalog")}
            className="mt-6 bg-[#FF5A00] text-white px-6 py-3 font-mono-data tracking-widest text-sm border-2 border-zinc-900 hard-shadow-sm"
          >
            BROWSE CATALOG →
          </button>
        </div>
      </div>
    );
  }

  const createOrder = async (e) => {
    e.preventDefault();
    if (address.trim().length < 10) {
      toast.error("Enter a complete delivery address");
      return;
    }
    setProcessing(true);
    try {
      const { data } = await api.post("/orders/create", {
        address,
        delivery_city: city,
      });
      setOrder(data);
      setStep("payment");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create order");
    } finally {
      setProcessing(false);
    }
  };

  const mockPay = async () => {
    setProcessing(true);
    // Simulate Razorpay checkout latency
    await new Promise((r) => setTimeout(r, 1600));
    try {
      const fakePaymentId = "pay_" + Math.random().toString(36).slice(2, 14);
      const fakeSig = "sig_" + Math.random().toString(36).slice(2, 20);
      const { data } = await api.post(`/orders/${order.id}/verify-payment`, {
        razorpay_order_id: order.razorpay_order_id,
        razorpay_payment_id: fakePaymentId,
        razorpay_signature: fakeSig,
      });
      setOrder(data);
      setStep("success");
      refresh();
      toast.success("Payment successful");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payment failed");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-100">
      <div className="px-6 lg:px-10 py-8">
        <button
          onClick={() => nav(-1)}
          className="inline-flex items-center gap-2 font-mono-data text-xs tracking-widest hover:text-[#FF5A00]"
        >
          <ArrowLeft className="w-4 h-4" /> BACK
        </button>
        <div className="tag mt-6">CHECKOUT</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight mt-1">
          {step === "address" && "Delivery details"}
          {step === "payment" && "Complete payment"}
          {step === "success" && "Order confirmed"}
        </h1>
      </div>

      <div className="px-6 lg:px-10 pb-16 grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3">
          {step === "address" && (
            <form
              onSubmit={createOrder}
              className="bg-white border-2 border-zinc-900 p-6 space-y-5"
              data-testid="address-form"
            >
              <div>
                <label className="tag block mb-2">Delivery address (factory / warehouse)</label>
                <textarea
                  data-testid="address-input"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  rows={4}
                  placeholder="Plot no, street, area, landmark, PIN"
                  className="w-full border-2 border-zinc-300 focus:border-[#FF5A00] outline-none px-4 py-3 font-medium"
                />
              </div>
              <div>
                <label className="tag block mb-2">City</label>
                <select
                  data-testid="city-select"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="w-full border-2 border-zinc-300 focus:border-[#FF5A00] outline-none px-4 py-3 font-medium"
                >
                  {["Mumbai", "Pune", "Ahmedabad", "Surat", "Nashik"].map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </div>

              <button
                type="submit"
                disabled={processing}
                data-testid="continue-to-payment"
                className="w-full bg-zinc-900 text-white py-4 font-mono-data tracking-widest text-sm border-2 border-zinc-900 hard-shadow-sm disabled:opacity-50"
              >
                {processing ? "CREATING ORDER..." : "CONTINUE TO PAYMENT →"}
              </button>
            </form>
          )}

          {step === "payment" && (
            <div className="bg-white border-2 border-zinc-900 p-6">
              <div className="flex items-center justify-between border-b border-zinc-200 pb-4">
                <div>
                  <div className="tag">SECURE PAYMENT · RAZORPAY</div>
                  <div className="font-display font-black text-2xl mt-1">
                    ₹{order.total.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </div>
                </div>
                <div className="flex items-center gap-1 text-xs font-mono-data text-green-700">
                  <Lock className="w-4 h-4" /> ENCRYPTED
                </div>
              </div>

              <div className="mt-6 space-y-3">
                <div className="border-2 border-[#FF5A00] p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-zinc-900 flex items-center justify-center">
                      <CreditCard className="w-5 h-5 text-[#FF5A00]" />
                    </div>
                    <div>
                      <div className="font-bold">UPI · Cards · Netbanking</div>
                      <div className="text-xs text-zinc-500 font-mono-data">
                        Powered by Razorpay (Test Mode)
                      </div>
                    </div>
                  </div>
                  <div className="text-xs font-mono-data text-green-700">SELECTED</div>
                </div>

                <div className="bg-amber-50 border-2 border-amber-400 p-3 font-mono-data text-xs">
                  <strong>DEMO MODE:</strong> This is a mocked Razorpay flow. Clicking Pay Now will simulate a successful transaction.
                </div>
              </div>

              <button
                onClick={mockPay}
                disabled={processing}
                data-testid="pay-now-button"
                className="mt-6 w-full bg-[#FF5A00] text-white py-4 font-mono-data tracking-widest text-sm border-2 border-zinc-900 hard-shadow-sm disabled:opacity-70 flex items-center justify-center gap-2"
              >
                {processing ? (
                  <>PROCESSING PAYMENT...</>
                ) : (
                  <>PAY ₹{order.total.toFixed(2)} NOW <Lock className="w-4 h-4" /></>
                )}
              </button>
            </div>
          )}

          {step === "success" && order && (
            <div className="bg-white border-2 border-zinc-900 p-8" data-testid="success-panel">
              <CheckCircle2 className="w-14 h-14 text-green-600" strokeWidth={1.5} />
              <div className="tag mt-4">ORDER PLACED</div>
              <h2 className="font-display text-3xl font-black tracking-tight mt-1">
                Payment received
              </h2>
              <p className="text-zinc-500 mt-2">
                Your order is being packed. Track it live below.
              </p>
              <div className="mt-6 grid grid-cols-2 gap-4 font-mono-data text-sm">
                <div className="border border-zinc-300 p-3">
                  <div className="tag">ORDER ID</div>
                  <div className="mt-1 truncate">{order.id.slice(0, 8)}...</div>
                </div>
                <div className="border border-zinc-300 p-3">
                  <div className="tag">PAYMENT ID</div>
                  <div className="mt-1 truncate">{order.razorpay_payment_id}</div>
                </div>
              </div>
              <button
                onClick={() => nav(`/order/${order.id}`)}
                data-testid="track-order-button"
                className="mt-6 w-full bg-zinc-900 text-white py-4 font-mono-data tracking-widest text-sm border-2 border-zinc-900 hard-shadow-sm"
              >
                TRACK ORDER LIVE →
              </button>
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="lg:col-span-2">
          <div className="bg-white border border-zinc-900 p-6 sticky top-24">
            <div className="tag mb-3">ORDER TOTAL</div>
            <div className="space-y-2 font-mono-data text-sm">
              {(order || cart).items.map((it) => (
                <div key={it.product_id} className="flex justify-between gap-3">
                  <span className="text-zinc-600 truncate">
                    {it.name || it.product?.name} × {it.quantity}
                  </span>
                  <span>₹{(it.line_total).toFixed(2)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-dashed border-zinc-300 mt-4 pt-4 space-y-2 font-mono-data text-sm">
              <div className="flex justify-between">
                <span className="text-zinc-500">Subtotal</span>
                <span>₹{(order || cart).subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">GST</span>
                <span>₹{(order || cart).tax.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Delivery</span>
                <span>{(order || cart).delivery_fee === 0 ? "FREE" : "₹" + (order || cart).delivery_fee.toFixed(2)}</span>
              </div>
            </div>
            <div className="border-t-2 border-zinc-900 border-dashed mt-4 pt-4 flex justify-between items-baseline">
              <span className="font-display font-black">TOTAL</span>
              <span className="font-mono-data font-bold text-xl">
                ₹{(order || cart).total.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </span>
            </div>
            <div className="mt-6 flex items-center gap-2 text-xs font-mono-data text-zinc-500">
              <ShieldCheck className="w-4 h-4 text-green-600" />
              100% authentic materials · Full MSME GST invoice
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
